import React from "react";
import { motion } from "framer-motion";
import { Phone, Heart, Check } from "lucide-react";
import { WEDDING } from "@/constants/testIds";
import { SITE, MEDIA } from "@/lib/site";

const BULLETS = [
  "Demande en mariage spectaculaire",
  "Cérémonie & ouverture de bal aérienne",
  "Logos, prénoms, dates illuminés dans le ciel",
];

export const Wedding = () => {
  return (
    <section
      id="mariage"
      data-testid={WEDDING.root}
      className="relative py-28 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img src={MEDIA.wedding} alt="Spectacle de drones pour mariage — prénoms et cœur dessinés dans le ciel" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030305] via-[#030305]/85 to-[#030305]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
              <Heart size={12} className="text-[#007AFF]" /> Mariages d'exception
            </div>

            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              Un ciel <span className="italic font-semibold">unique</span><br />
              pour un <span className="text-[#007AFF]">oui</span> inoubliable.
            </h2>

            <p className="font-body mt-6 text-white/70 leading-relaxed max-w-xl">
              Pour vos demandes en mariage, cérémonies ou ouvertures de bal, nos drones
              dessinent vos prénoms, dates et symboles d'amour à 120 m d'altitude.
              Une signature aérienne pensée pour les mariages haut de gamme partout en France.
            </p>

            <ul className="mt-8 space-y-3">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/80">
                  <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#007AFF]/15 text-[#007AFF]">
                    <Check size={12} />
                  </span>
                  <span className="text-sm md:text-base">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <div className="rounded-2xl glass-strong p-5 flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">À partir de</div>
                <div className="font-display text-4xl md:text-5xl font-light mt-1">
                  8 000<span className="text-[#007AFF]">€</span>
                </div>
                <div className="text-xs text-white/40 mt-1">TVA incluse · 100 drones</div>
              </div>
              <a
                data-testid={WEDDING.callAnna}
                href={SITE.phoneLink}
                className="group flex-1 rounded-2xl bg-[#007AFF] p-5 text-white hover:shadow-[0_0_36px_rgba(0,122,255,0.55)] transition-all"
              >
                <div className="text-xs uppercase tracking-[0.2em] opacity-80">Appelez Valérie · Paris</div>
                <div className="flex items-center gap-3 mt-2">
                  <Phone size={18} />
                  <span className="font-display text-xl font-semibold">{SITE.phoneDisplay}</span>
                </div>
                <div className="text-xs opacity-80 mt-2 group-hover:translate-x-1 transition-transform">
                  Disponible 7j/7 · Lun-Dim 9h-21h →
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right: video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9 }}
            className="relative aspect-video rounded-3xl overflow-hidden glass-strong"
          >
            <div className="absolute -inset-px rounded-3xl bg-gradient-to-tr from-[#007AFF]/30 via-transparent to-transparent pointer-events-none" />
            <iframe
              data-testid={WEDDING.video}
              src={`https://www.youtube.com/embed/${SITE.weddingYoutubeId}?rel=0&modestbranding=1`}
              title="Mariage spectacle de drones"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Wedding;
